import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import TcnApp from '../../../components/tcn/TcnApp';
import TopBar from '../../../components/tcn/layout/TopBar';
import { Card, Icon, SectionLabel, AccountAvatar } from '../../../components/tcn/ui-kit';
import AlertBell from '../../../components/tcn-modules/m7/AlertBell';
import { TCN } from '../../../components/tcn/ui-kit/tokens';
import CreatorListRow from '../../../components/tcn-modules/m4/CreatorListRow';
import CreatorFilters from '../../../components/tcn-modules/m4/CreatorFilters';
import ActivenessDistributionMini from '../../../components/tcn-modules/m4/ActivenessDistributionMini';
import OneOnOnePlanner from '../../../components/tcn-modules/m4/OneOnOnePlanner';
import CreatorsSectionTabs from '../../../components/tcn-modules/shared/CreatorsSectionTabs';
import { formatInt } from '../../../components/tcn-modules/shared/format';
import useIsDesktop from '../../../hooks/useIsDesktop';

/**
 * /tcn/me/creators — Module 4 — Creators list.
 *
 * Single SELECT on creator_current_month filtered by manager_id = auth.uid()
 * AND status != 'departed'. Default sort by priority_score_risk desc.
 *
 * Note (Phase A limitation): creator_current_month keeps financial fields
 * encrypted because super_admin can SELECT this table (R2 — see ccm_super_admin_select).
 * Bulk-decrypting every creator just to render the list would require a heavy
 * Edge Function call on every load (50 RPC roundtrips for a 50-creator roster).
 *
 * Strategy:
 *   - List view shows clear-text fields only: alert_status, priority_category,
 *     tier_current, activeness_level, rank_up_eligibility_7d15h, days_since_joining.
 *   - Diamonds appear as "—" until the user clicks a row, which loads the
 *     full Detail page that calls `tcn-decrypt-creator` for that single creator.
 *   - Sort fallback: priority_score_risk_enc cannot be decrypted in-list, so we
 *     sort by alert_status (critical → at_risk → none) + days_since_joining.
 */
const ALERT_RANK = { critical: 3, at_risk: 2, none: 1 };

const TcnCreatorsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const isDesktop = useIsDesktop();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialGap = searchParams.get('gap');
  const initialTier = searchParams.get('tier');
  const planner = searchParams.get('planner') === '1on1';
  const [busy1on1, setBusy1on1] = useState(null);

  const setPlanner = (on) => {
    const p = new URLSearchParams(searchParams);
    if (on) p.set('planner', '1on1');
    else p.delete('planner');
    setSearchParams(p, { replace: true });
  };

  const userName = userProfile?.full_name || userProfile?.display_name || userProfile?.email || 'Agent';

  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState('alert_desc');
  const [search, setSearch] = useState('');

  // Pre-set a filter when arriving from Strategic Focus CTA or M3 tier drill-down
  useEffect(() => {
    const next = {};
    if (initialGap === 'rank_up_eligibility_rate_7d15h') {
      next.rank_up_eligibility_7d15h = false;
    }
    // Audit fix #46 — M3 TierRow tap drills here with ?tier=N
    if (initialTier !== null && initialTier !== undefined) {
      const t = parseInt(initialTier, 10);
      if (Number.isFinite(t) && t >= 1 && t <= 10) next.tier = t;
    }
    if (Object.keys(next).length > 0) setFilters(next);
  }, [initialGap, initialTier]);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('creator_current_month')
      .select('*')
      .eq('manager_id', user.id)
      .neq('status', 'departed');
    if (error) {
      console.error('[TcnCreatorsList] load error', error);
      setCreators([]);
    } else {
      setCreators(data || []);
    }
    setLoading(false);
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  // A4.6 — toggle a creator in/out of today's 1-on-1 list. Mirrors the detail
  // page handler: optimistic update + set_creator_1on1 RPC, and on add also logs
  // to daily_1on1_history + action_log (one_on_one_call) so it surfaces in M6.
  const toggle1on1 = useCallback(
    async (creator, next) => {
      if (!creator || !user?.id) return;
      setBusy1on1(creator.creator_id);
      setCreators((cs) =>
        cs.map((c) =>
          c.creator_id === creator.creator_id ? { ...c, is_in_today_1on1_list: next } : c,
        ),
      );
      const { error } = await supabase.rpc('set_creator_1on1', {
        p_creator_id: creator.creator_id,
        p_value: next,
      });
      if (error) {
        setCreators((cs) =>
          cs.map((c) =>
            c.creator_id === creator.creator_id ? { ...c, is_in_today_1on1_list: !next } : c,
          ),
        );
        console.error('[1on1 toggle] error', error);
      } else if (next) {
        const today = new Date().toISOString().slice(0, 10);
        const username = creator.tiktok_username || creator.display_name || 'creator';
        try {
          await supabase.from('daily_1on1_history').insert({
            tenant_id: creator.tenant_id,
            agency_id: creator.agency_id,
            agent_id: user.id,
            creator_id: creator.creator_id,
          });
        } catch {
          /* ignore — RLS/table may reject */
        }
        try {
          await supabase.from('action_log').insert({
            tenant_id: creator.tenant_id,
            agency_id: creator.agency_id,
            agent_id: user.id,
            source_activity: 'one_on_one_call',
            source_entity_type: 'creator',
            source_entity_id: creator.creator_id,
            title: `1-on-1 with @${username}`,
            description: 'Scheduled from 1-on-1 planner',
            due_date: today,
            status: 'open',
          });
        } catch {
          /* ignore */
        }
      }
      setBusy1on1(null);
    },
    [user?.id],
  );

  const filtered = useMemo(() => {
    let rows = creators;
    if (filters.tier) rows = rows.filter((c) => c.tier_current === filters.tier);
    if (filters.alert_status) rows = rows.filter((c) => c.alert_status === filters.alert_status);
    if (filters.rank_up_eligibility_7d15h !== null && filters.rank_up_eligibility_7d15h !== undefined) {
      rows = rows.filter(
        (c) => Boolean(c.rank_up_eligibility_7d15h) === Boolean(filters.rank_up_eligibility_7d15h)
      );
    }
    if (filters.activeness_level) rows = rows.filter((c) => c.activeness_level === filters.activeness_level);
    if (filters.priority_category) {
      rows = rows.filter((c) => c.priority_category === filters.priority_category);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(
        (c) =>
          (c.tiktok_username || '').toLowerCase().includes(q) ||
          (c.display_name || '').toLowerCase().includes(q)
      );
    }
    // Sort
    const sorted = [...rows];
    if (sort === 'alert_desc') {
      sorted.sort(
        (a, b) =>
          (ALERT_RANK[b.alert_status] || 0) - (ALERT_RANK[a.alert_status] || 0) ||
          (a.tier_current || 0) - (b.tier_current || 0)
      );
    } else if (sort === 'tier_desc') {
      sorted.sort((a, b) => (b.tier_current || 0) - (a.tier_current || 0));
    } else if (sort === 'tier_asc') {
      sorted.sort((a, b) => (a.tier_current || 0) - (b.tier_current || 0));
    } else if (sort === 'days_asc') {
      sorted.sort((a, b) => (a.days_since_joining ?? 9999) - (b.days_since_joining ?? 9999));
    }
    return sorted;
  }, [creators, filters, search, sort]);

  const handleTabChange = (tab) => {
    switch (tab) {
      case 'today':    return navigate('/tcn/me');
      case 'creators': return navigate('/tcn/me/creators');
      case 'tiers':    return navigate('/tcn/me/tiers');
      case 'payout':   return navigate('/tcn/me/payout');
      case 'actions':  return navigate('/tcn/me/actions');
      default:         return navigate('/tcn/me');
    }
  };

  const totalCount = creators.length;

  return (
    <TcnApp
      tab="creators"
      onTabChange={handleTabChange}
      desktopBreadcrumb={[t('tcn.tabs.creators', 'Creators')]}
      mobileHeader={
        <TopBar
          leading={<AccountAvatar name={userName} size={36} ring />}
          trailing={<AlertBell />}
          title={t('tcn.m4.title', 'My Creators')}
          subtitle={t('tcn.m4.subtitle', '{{count}} active', { count: totalCount })}
          largeTitle
        />
      }
    >
      <div
        style={{
          maxWidth: isDesktop ? 1200 : '100%',
          padding: isDesktop ? 0 : '0 0 12px',
        }}
      >
        {/* Entry segment [Creators | New creators] (audit §0.2) */}
        <div style={{ padding: isDesktop ? '0 0 12px' : '0 14px 12px' }}>
          <CreatorsSectionTabs active="creators" isDesktop={isDesktop} />
        </div>

        {/* Mode switch — All creators / Today's 1-on-1 (A4.6) */}
        <div
          style={{
            display: 'flex',
            gap: 6,
            padding: isDesktop ? '0 0 12px' : '0 14px 12px',
          }}
        >
          {[
            { id: 'list', label: t('tcn.m4.mode.all', 'All creators') },
            {
              id: 'planner',
              label: `${t('tcn.m4.mode.oneOnOne', "Today's 1-on-1")} · ${
                creators.filter((c) => c.is_in_today_1on1_list).length
              }`,
            },
          ].map((m) => {
            const active = (m.id === 'planner') === planner;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setPlanner(m.id === 'planner')}
                style={{
                  padding: '7px 14px',
                  borderRadius: 8,
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 12,
                  fontWeight: 600,
                  background: active ? TCN.surface3 : TCN.surface1,
                  border: `1px solid ${active ? TCN.cyan : TCN.border}`,
                  color: active ? TCN.text : TCN.textDim,
                }}
              >
                {m.label}
              </button>
            );
          })}
        </div>

        {planner ? (
          <div style={{ padding: isDesktop ? 0 : '0 14px' }}>
            <OneOnOnePlanner creators={creators} onToggle={toggle1on1} busyId={busy1on1} />
          </div>
        ) : (
          <>
        {/* Header strip + activeness mini */}
        <div
          style={{
            padding: isDesktop ? 0 : '0 14px 12px',
            display: 'grid',
            gridTemplateColumns: isDesktop ? '2fr 1fr' : '1fr',
            gap: 12,
            marginBottom: 12,
          }}
        >
          <Card>
            <SectionLabel>{t('tcn.m4.summary', 'Summary')}</SectionLabel>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 10,
                marginTop: 8,
              }}
            >
              <SummaryCell label={t('tcn.m4.total', 'Total')} value={formatInt(totalCount)} />
              <SummaryCell
                label={t('tcn.m4.alert.at_risk', 'At risk')}
                value={formatInt(creators.filter((c) => c.alert_status === 'at_risk').length)}
                color={TCN.amber}
              />
              <SummaryCell
                label={t('tcn.m4.alert.critical', 'Critical')}
                value={formatInt(creators.filter((c) => c.alert_status === 'critical').length)}
                color={TCN.red}
              />
            </div>
          </Card>
          <Card>
            <ActivenessDistributionMini creators={creators} />
          </Card>
        </div>

        {/* Search bar */}
        <div style={{ padding: isDesktop ? '0 0 8px' : '0 14px 8px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 12px',
              background: TCN.surface1,
              border: `1px solid ${TCN.border}`,
              borderRadius: 12,
            }}
          >
            <Icon name="search" size={16} color={TCN.textDim} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('tcn.m4.searchPh', 'Search creator…')}
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                color: TCN.text,
                // FRM-001/TCH-001 — 16px sur mobile pour bloquer le zoom iOS au focus ;
                // densité desktop préservée à 13px.
                fontSize: isDesktop ? 13 : 16,
                outline: 'none',
              }}
            />
            {/* Sort selector */}
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              style={{
                background: 'transparent',
                border: 'none',
                color: TCN.cyan,
                // FRM-001/TCH-001 — 16px sur mobile (anti-zoom iOS), 12px desktop.
                fontSize: isDesktop ? 12 : 16,
                cursor: 'pointer',
                outline: 'none',
                fontFamily: 'inherit',
              }}
            >
              <option value="alert_desc">{t('tcn.m4.sort.alertDesc', 'Risk first')}</option>
              <option value="tier_desc">{t('tcn.m4.sort.tierDesc', 'Tier high→low')}</option>
              <option value="tier_asc">{t('tcn.m4.sort.tierAsc', 'Tier low→high')}</option>
              <option value="days_asc">{t('tcn.m4.sort.daysAsc', 'Newest first')}</option>
            </select>
          </div>
        </div>

        {/* Filters */}
        <CreatorFilters filters={filters} onChange={setFilters} isDesktop={isDesktop} />

        {/* List/table */}
        {loading ? (
          <div style={{ padding: 20, textAlign: 'center', color: TCN.textDim }}>
            {t('tcn.m4.loading', 'Loading creators…')}
          </div>
        ) : filtered.length === 0 ? (
          <Card style={{ margin: isDesktop ? '12px 0' : '0 14px' }}>
            <div style={{ textAlign: 'center', padding: 24, color: TCN.textDim }}>
              <Icon name="people" size={32} color={TCN.textMute} />
              <div style={{ marginTop: 10, fontSize: 14, color: TCN.text }}>
                {t('tcn.m4.empty', 'No creator matches your filters.')}
              </div>
            </div>
          </Card>
        ) : isDesktop ? (
          <Card padded={false}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead
                style={{
                  position: 'sticky',
                  top: 0,
                  background: TCN.surface2,
                  zIndex: 1,
                }}
              >
                <tr>
                  <Th>{t('tcn.m4.col.creator', 'Creator')}</Th>
                  <Th>{t('tcn.m4.col.tier', 'Tier')}</Th>
                  <Th>{t('tcn.m4.col.diamondsMtd', 'Diamonds MTD')}</Th>
                  <Th>{t('tcn.m4.col.alert', 'Alert')}</Th>
                  <Th>{t('tcn.m4.col.priority', 'Priority')}</Th>
                  <Th>{t('tcn.m4.col.activeness', 'Activeness')}</Th>
                  <Th>{t('tcn.m4.col.rankUp', 'Rank-up')}</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <CreatorListRow
                    key={c.creator_id}
                    creator={c}
                    asTable
                    onClick={() => navigate(`/tcn/me/creators/${c.creator_id}`)}
                  />
                ))}
              </tbody>
              {/* Sticky totals */}
              <tfoot>
                <tr style={{ background: TCN.surface2, borderTop: `1px solid ${TCN.borderStrong}` }}>
                  <td
                    style={{
                      padding: '12px 16px',
                      fontSize: 12,
                      fontWeight: 700,
                      color: TCN.textDim,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                    }}
                    colSpan={7}
                  >
                    {t('tcn.m4.totals', 'Showing {{n}} of {{total}}', {
                      n: filtered.length,
                      total: totalCount,
                    })}
                  </td>
                </tr>
              </tfoot>
            </table>
          </Card>
        ) : (
          <div>
            {filtered.map((c) => (
              <CreatorListRow
                key={c.creator_id}
                creator={c}
                onClick={() => navigate(`/tcn/me/creators/${c.creator_id}`)}
              />
            ))}
            {/* Sticky totals on mobile */}
            <div
              style={{
                padding: '12px 14px',
                background: TCN.surface2,
                fontSize: 12,
                color: TCN.textDim,
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                textAlign: 'center',
              }}
            >
              {t('tcn.m4.totals', 'Showing {{n}} of {{total}}', {
                n: filtered.length,
                total: totalCount,
              })}
            </div>
          </div>
        )}
          </>
        )}
      </div>
    </TcnApp>
  );
};

const Th = ({ children }) => (
  <th
    style={{
      textAlign: 'start',
      padding: '10px 16px',
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: TCN.textDim,
      borderBottom: `1px solid ${TCN.border}`,
    }}
  >
    {children}
  </th>
);

const SummaryCell = ({ label, value, color }) => (
  <div>
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: TCN.textMute,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: 22,
        fontWeight: 700,
        color: color ?? TCN.text,
        letterSpacing: '-0.04em',
        fontVariantNumeric: 'tabular-nums',
        marginTop: 2,
      }}
    >
      {value}
    </div>
  </div>
);

export default TcnCreatorsList;
